<template>
    <price-tag-shell :context="context">
        <div class="tiered" :style="layoutStyle">
            <div class="pt-column tiered__product">
                <div v-if="context.isMixAndMatch" class="pt-chip">
                    {{ tagText('mixAndMatch') }}
                </div>
                <div v-if="fieldsByKey.tierCount" class="pt-medium tiered__count">
                    <template v-if="editable">
                        {{ tagText('tiers') }}
                        <price-tag-value v-bind="bindField('tierCount')" />
                    </template>
                    <template v-else
                        >{{ tagText('tiers') }} {{ selectedTierCount }}</template
                    >
                </div>
                <price-tag-product :product="context.product" size="medium" />
                <div v-if="context.regularPrice" class="pt-column">
                    <span class="pt-small">{{ tagText('regular') }}</span>
                    <span class="pt-currency">
                        {{ formatMoney(context.regularPrice) }} {{ context.currencySymbol }}
                    </span>
                </div>
            </div>
            <div
                v-for="(tier, tierIndex) in visibleTiers"
                :key="tierIndex"
                class="pt-column pt-grow tiered__tier"
            >
                <div class="pt-medium">
                    <template v-if="isTierBuyEditable(tierIndex)">
                        {{ tagText('buy') }}
                        <price-tag-value v-bind="bindField(buyKey(tierIndex))" />
                    </template>
                    <template v-else>{{ rangeText(tierIndex) }}</template>
                </div>
                <price-tag-discount :percent="tier.discountPercent">
                    <template v-if="isTierPercentEditable(tierIndex)">
                        <price-tag-value v-bind="bindField(percentKey(tierIndex))" />%
                    </template>
                </price-tag-discount>
                <div class="pt-currency">{{ formatMoney(tier.promoPrice) }}</div>
                <div class="pt-small">
                    {{ tagText('each', { currency: context.currencySymbol }) }}
                </div>
            </div>
        </div>
    </price-tag-shell>
</template>

<script setup>
import { computed } from 'vue';
import { isNil } from 'lodash';
import {
    priceTagLayoutProps,
    priceTagLayoutEmits,
    usePriceTagLayout,
} from '@/js/composables/usePriceTagLayout';

const props = defineProps(priceTagLayoutProps);
const emit = defineEmits(priceTagLayoutEmits);

const { bindField, tagText, formatMoney } = usePriceTagLayout(props, emit);

const buyKey = tierIndex => `tier${tierIndex + 1}Buy`;
const percentKey = tierIndex => `tier${tierIndex + 1}Percent`;

const tierCountFromFields = computed(() =>
    Object.keys(props.fieldsByKey || {}).reduce((maxTier, key) => {
        const match = /^tier([0-9]{1,2})(Buy|Percent)$/.exec(key);
        if (!match) return maxTier;
        return Math.max(maxTier, Number(match[1]));
    }, 0)
);

const clampTierCount = value => {
    const max = Math.max(
        Number(props.fieldsByKey?.tierCount?.max) || tierCountFromFields.value || 1,
        1
    );
    const min = Math.max(Number(props.fieldsByKey?.tierCount?.min) || 1, 1);
    return Math.min(Math.max(value, min), max);
};

const selectedTierCount = computed(() => {
    const value = Number(props.values.tierCount);
    if (!Number.isNaN(value) && Number.isFinite(value)) {
        return clampTierCount(Math.floor(value));
    }

    const contextCount = Array.isArray(props.context.tiers) ? props.context.tiers.length : 0;
    if (contextCount > 0) return clampTierCount(contextCount);

    return clampTierCount(Math.max(tierCountFromFields.value, 1));
});

const tierCount = computed(() => {
    const countFromContext = Array.isArray(props.context.tiers) ? props.context.tiers.length : 0;
    const fallbackCount = Math.max(countFromContext, tierCountFromFields.value, 1);
    return props.fieldsByKey.tierCount ? selectedTierCount.value : fallbackCount;
});

const visibleTiers = computed(() =>
    Array.from({ length: tierCount.value }, (_, tierIndex) => props.context.tiers[tierIndex] || {})
);

const layoutStyle = computed(() => {
    const count = tierCount.value;
    let productColumnWidth = '12rem';
    let discountFontSize = '3rem';
    if (count <= 3) {
        productColumnWidth = '13.5rem';
        discountFontSize = '3.6rem';
    }
    if (count <= 2) {
        productColumnWidth = '16rem';
        discountFontSize = '4.2rem';
    }
    const mediumFontSize = count <= 3 ? '2.4rem' : '1.8rem';
    const currencyFontSize = count <= 3 ? '3.2rem' : '2.6rem';

    return {
        gridTemplateColumns: `${productColumnWidth} repeat(${count}, minmax(0, 1fr))`,
        '--tier-discount-font-size': discountFontSize,
        '--tier-medium-font-size': mediumFontSize,
        '--tier-currency-font-size': currencyFontSize,
    };
});

const isTierBuyEditable = tierIndex => props.editable && !!props.fieldsByKey[buyKey(tierIndex)];

const isTierPercentEditable = tierIndex =>
    props.editable && !!props.fieldsByKey[percentKey(tierIndex)];

const tierBuy = tierIndex => {
    const value = props.values[buyKey(tierIndex)];
    return isNil(value) ? (visibleTiers.value[tierIndex] || {}).buyNumber : value;
};

const rangeText = tierIndex => {
    const from = tierBuy(tierIndex);
    if (isNil(from)) return tagText('buyFrom', { from: tierIndex + 1 });
    const nextFrom = tierBuy(tierIndex + 1);
    if (isNil(nextFrom)) return tagText('buyFrom', { from });
    const to = Math.max(from, nextFrom - 1);
    return to === from ? `${tagText('buy')} ${from}` : tagText('buyRange', { from, to });
};
</script>

<style lang="scss" scoped>
@import '@style/price-tags/price-tag-layout';

.tiered {
    display: grid;
    align-items: start;

    &__product {
        min-width: 0;
        padding-right: 1rem;

        .tiered__count {
            display: flex;
            align-items: center;
            gap: 0.6rem;
            font-size: 1.8rem;
        }

        :deep(.price-tag-product__brand) {
            font-size: 2.8rem;
        }

        :deep(.price-tag-product__name) {
            font-size: 1.8rem;
        }
    }

    &__tier {
        min-width: 0;
        align-items: center;
        text-align: center;
        padding: 0 0.6rem;
        border-left: 0.3rem solid currentColor;

        .pt-medium {
            font-size: var(--tier-medium-font-size);
            white-space: nowrap;
        }

        .pt-currency {
            font-size: var(--tier-currency-font-size);
            white-space: nowrap;
        }

        :deep(.price-tag-discount) {
            align-self: center;
            justify-content: center;
            width: 100%;
            padding: 0.4rem 0.6rem;
            font-size: var(--tier-discount-font-size);
            line-height: 1;
        }

        :deep(.price-tag-value__input) {
            max-width: 8ch;
        }
    }
}
</style>
